import jsYaml from 'js-yaml';
import { isEmpty } from 'lodash-es';
import { markRaw } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/lib/utils/vue3compat/vue.js?vue3';
import { GlForm, GlButton, GlButtonGroup, GlCollapsibleListbox, GlFormGroup, GlFormInput, GlFormSelect, GlTooltipDirective, GlDisclosureDropdown, GlModal, GlFormTextarea, GlToggle } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import produce from 'immer';
import { getDraft, clearDraft, updateDraft, getLockVersion } from '~/lib/utils/autosave';
import csrf from '~/lib/utils/csrf';
import { __, s__, sprintf } from '~/locale';
import Tracking from '/builds/gitlab/gitlab-ee/app/assets/javascripts/tracking/index.js?vue3';
import glFeatureFlagsMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import MarkdownEditor from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/markdown/markdown_editor.vue?vue3';
import { trackSavedUsingEditor } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/markdown/tracking.js?vue3';
import { getInitialEditingMode } from '~/vue_shared/components/markdown/utils';
import { EDITING_MODE_CONTENT_EDITOR } from '~/vue_shared/constants';
import { ActionCableProvider } from '~/collaborative_editing';
import { confirmAction } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/lib/utils/confirm_via_gl_modal/confirm_action.js?vue3';
import CollaboratorsIndicator from '/builds/gitlab/gitlab-ee/app/assets/javascripts/collaborative_editing/components/collaborators_indicator.vue?vue3';
import WikiSidebarToggle from '/builds/gitlab/gitlab-ee/app/assets/javascripts/wikis/components/wiki_sidebar_toggle.vue?vue3';
import { formatDate } from '~/lib/utils/datetime/date_format_utility';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { WIKI_CONTENT_EDITOR_TRACKING_LABEL, WIKI_FORMAT_LABEL, WIKI_FORMAT_UPDATED_ACTION, CONTENT_EDITOR_LOADED_ACTION, WIKI_TEMPLATES_DIR } from '../constants';
import { isTemplate as isTemplateUrl } from '../utils';
import getAutoCommitMessagePreference from '../graphql/auto_commit_message_preference.query.graphql';
import updateAutoCommitMessagePreference from '../graphql/update_auto_commit_message_preference.mutation.graphql';
import WikiTemplate from '/builds/gitlab/gitlab-ee/app/assets/javascripts/wikis/components/wiki_template.vue?vue3';
import DeleteWikiModal from '/builds/gitlab/gitlab-ee/app/assets/javascripts/wikis/components/delete_wiki_modal.vue?vue3';
const trackingMixin = Tracking.mixin({
    label: WIKI_CONTENT_EDITOR_TRACKING_LABEL
});
const SAVE_MESSAGE = {
    AUTO: 'AUTO',
    CUSTOM: 'CUSTOM'
};
const AUTO_GENERATED_PATH_DATE_FORMAT = 'yyyymmddHHMMss';
function getPagePath(pageInfo) {
    return pageInfo.persisted ? pageInfo.path : pageInfo.createPath;
}
const autosaveKey = (pageInfo, field)=>{
    const path = pageInfo.persisted ? pageInfo.path : pageInfo.createPath;
    return `${path}/${field}`;
};
const titleAutosaveKey = (pageInfo)=>autosaveKey(pageInfo, 'title');
const formatAutosaveKey = (pageInfo)=>autosaveKey(pageInfo, 'format');
const contentAutosaveKey = (pageInfo)=>autosaveKey(pageInfo, 'content');
const commitAutosaveKey = (pageInfo)=>autosaveKey(pageInfo, 'commit');
const isDraftStale = (pageInfo)=>{
    if (!pageInfo.persisted) return false;
    const storedLockVersion = getLockVersion(titleAutosaveKey(pageInfo));
    return Boolean(storedLockVersion) && storedLockVersion !== pageInfo.lastCommitSha;
};
const readDraft = (pageInfo, key)=>isDraftStale(pageInfo) ? null : getDraft(key);
const getTitle = (pageInfo, frontMatter)=>{
    const autosavedTitle = readDraft(pageInfo, titleAutosaveKey(pageInfo));
    const frontMatterTitle = frontMatter?.title?.trim();
    const pageInfoTitle = pageInfo.title?.trim();
    return autosavedTitle || frontMatterTitle || pageInfoTitle || '';
};
const getFormat = (pageInfo)=>readDraft(pageInfo, formatAutosaveKey(pageInfo)) || pageInfo.format || 'markdown';
const getContent = (pageInfo)=>readDraft(pageInfo, contentAutosaveKey(pageInfo)) || pageInfo.content || '';
const getCommitMessage = (pageInfo)=>readDraft(pageInfo, commitAutosaveKey(pageInfo)) || pageInfo.commitMessage || '';
const getIsFormDirty = (pageInfo)=>Boolean(readDraft(pageInfo, titleAutosaveKey(pageInfo)));
export default {
    name: 'WikiForm',
    i18n: {
        title: {
            label: s__('WikiPage|Title'),
            placeholder: s__('WikiPage|Page title'),
            templatePlaceholder: s__('WikiPage|Template title'),
            newPagePlaceholder: s__('WikiPage|{Give this page a title}'),
            defaultTitle: __('Untitled')
        },
        path: {
            label: s__('WikiPage|Path'),
            placeholder: s__('WikiPage|Page path'),
            description: s__('WikiPage|The path where your page is located. Use "/" to create nested pages.'),
            generateFromTitle: s__('WikiPage|Generate path from title'),
            generateFromTitleHelp: s__('WikiPage|Automatically updates the path when you change the title.'),
            templateTitleHelp: s__('WikiPage|For templates, the path is generated from the title and cannot be edited.'),
            restorePagePath: s__('WikiPage|Convert back to page')
        },
        format: {
            label: s__('WikiPage|Format')
        },
        template: {
            label: __('Template'),
            collaborating: s__('WikiPage|Templates cannot be applied while others are editing this page.')
        },
        content: {
            label: s__('WikiPage|Content'),
            placeholder: s__('WikiPage|Write your content or drag files here…')
        },
        commitMessage: {
            label: s__('WikiPage|Commit message'),
            value: {
                existingPage: s__('WikiPage|Update %{pageTitle}'),
                newPage: s__('WikiPage|Create %{pageTitle}'),
                existingTemplate: s__('WikiPage|Update template %{pageTitle}'),
                newTemplatePage: s__('WikiPage|Create template %{pageTitle}')
            }
        },
        submitButton: {
            existingPage: s__('WikiPage|Save changes'),
            newPage: s__('WikiPage|Create page'),
            newSidebar: s__('WikiPage|Create sidebar'),
            newTemplate: s__('WikiPage|Create template'),
            existingTemplate: s__('WikiPage|Save template')
        },
        cancel: s__('WikiPage|Cancel'),
        messageModalTitle: s__('WikiPage|Add a commit message'),
        autoCommitMessageToggle: s__('WikiPage|Use the default commit message for future saves'),
        autoCommitMessageToggleHelp: s__('WikiPage|Wiki pages save without opening this dialog.'),
        deleteSidebar: s__('WikiPage|Delete sidebar')
    },
    components: {
        WikiSidebarToggle,
        GlForm,
        GlFormGroup,
        GlFormInput,
        GlFormSelect,
        GlButton,
        MarkdownEditor,
        WikiTemplate,
        DeleteWikiModal,
        GlButtonGroup,
        GlCollapsibleListbox,
        GlModal,
        GlFormTextarea,
        GlDisclosureDropdown,
        GlToggle,
        CollaboratorsIndicator
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    mixins: [
        trackingMixin,
        glFeatureFlagsMixin()
    ],
    inject: [
        'formatOptions',
        'pageInfo',
        'drawioUrl',
        'templates',
        'wikiUrl'
    ],
    emits: [
        'is-editing'
    ],
    saveOptions: [
        {
            text: s__('WikiPage|Save changes directly'),
            description: s__('WikiPage|Uses the default commit message'),
            value: SAVE_MESSAGE.AUTO
        },
        {
            text: s__('WikiPage|Save changes with message'),
            description: s__('WikiPage|Review and write a commit message'),
            value: SAVE_MESSAGE.CUSTOM
        }
    ],
    data () {
        const title = window.location.href.includes('random_title=true') ? '' : getTitle(this.pageInfo, this.pageInfo.frontMatter);
        const path = window.location.href.includes('random_title=true') ? '' : this.pageInfo.slug;
        return {
            title,
            pageTitle: title.replace('templates/', ''),
            format: getFormat(this.pageInfo),
            path,
            frontMatter: this.pageInfo.frontMatter || {},
            content: getContent(this.pageInfo),
            commitMessage: getCommitMessage(this.pageInfo),
            isContentEditorActive: false,
            isFormDirty: getIsFormDirty(this.pageInfo),
            formFieldProps: {
                placeholder: this.$options.i18n.content.placeholder,
                'aria-label': this.$options.i18n.content.label,
                id: 'wiki_content',
                class: 'note-textarea'
            },
            shouldGeneratePathFromTitle: !this.pageInfo.persisted && (!path.startsWith(`${WIKI_TEMPLATES_DIR}/`) || path.endsWith('{new_page_title}')) || this.isAutoGeneratedPath(path),
            initialPath: path,
            placeholderActive: false,
            placeholderText: this.$options.i18n.title.newPagePlaceholder,
            parentPath: '',
            useAutoCommitMessage: false,
            savingPreference: false,
            commitMessageModalOpen: false,
            isTemplateUrl: isTemplateUrl(),
            isSubmitting: false,
            collaborationProvider: null,
            plainTextStartContent: this.pageInfo.content || ''
        };
    },
    apollo: {
        useAutoCommitMessage: {
            query: getAutoCommitMessagePreference,
            update: (data)=>{
                return data.currentUser?.userPreferences?.wikiUseAutoCommitMessage ?? false;
            }
        }
    },
    computed: {
        isCollaborating () {
            return Boolean(this.collaborationProvider);
        },
        templateDescription () {
            return this.isCollaborating ? this.$options.i18n.template.collaborating : null;
        },
        isTemplatePath () {
            return this.path.startsWith(`${WIKI_TEMPLATES_DIR}/`);
        },
        isTemplateByPathOnly () {
            return this.isTemplatePath && !this.isTemplateUrl;
        },
        isTemplate () {
            return this.isTemplateUrl || this.isTemplatePath;
        },
        titlePlaceholder () {
            return this.isTemplate ? this.$options.i18n.title.templatePlaceholder : this.$options.i18n.title.placeholder;
        },
        autocompleteDataSources () {
            return gl.GfmAutoComplete?.dataSources;
        },
        csrfToken () {
            return csrf.token;
        },
        formAction () {
            return getPagePath(this.pageInfo);
        },
        commitMessageI18n () {
            if (this.pageInfo.persisted) {
                if (this.isTemplate) return this.$options.i18n.commitMessage.value.existingTemplate;
                return this.$options.i18n.commitMessage.value.existingPage;
            }
            if (this.isTemplate) return this.$options.i18n.commitMessage.value.newTemplatePage;
            return this.$options.i18n.commitMessage.value.newPage;
        },
        submitButtonText () {
            if (this.isTemplate) {
                return this.pageInfo.persisted ? this.$options.i18n.submitButton.existingTemplate : this.$options.i18n.submitButton.newTemplate;
            }
            let buttonText = this.pageInfo.persisted ? this.$options.i18n.submitButton.existingPage : this.$options.i18n.submitButton.newPage;
            buttonText = this.isCustomSidebar && !this.pageInfo.persisted ? this.$options.i18n.submitButton.newSidebar : buttonText;
            return buttonText;
        },
        isMarkdownFormat () {
            return this.format === 'markdown';
        },
        drawioEnabled () {
            return typeof this.drawioUrl === 'string' && this.drawioUrl.length > 0;
        },
        isCustomSidebar () {
            return this.wikiUrl.endsWith('_sidebar');
        },
        rawContent () {
            const serializedFrontMatter = this.isTemplate || isEmpty(this.frontMatter) ? '' : `---\n${jsYaml.safeDump(this.frontMatter, {
                skipInvalid: true
            })}---\n`;
            return `${serializedFrontMatter}${this.content}`;
        },
        messageModalAction () {
            return {
                primary: {
                    text: this.submitButtonText,
                    attributes: {
                        variant: 'confirm',
                        'data-testid': 'wiki-confirm-message'
                    }
                },
                cancel: {
                    text: this.$options.i18n.cancel
                }
            };
        },
        saveMessageMode () {
            return this.useAutoCommitMessage ? SAVE_MESSAGE.AUTO : SAVE_MESSAGE.CUSTOM;
        },
        collaborativeEditingEnabled () {
            return Boolean(this.glFeatures.wikiCollaborativeEditing && this.isMarkdownFormat && this.pageInfo.persisted);
        }
    },
    watch: {
        title () {
            this.updateCommitMessage();
        },
        pageTitle () {
            this.title = this.isTemplate && !this.pageInfo.persisted ? `templates/${this.pageTitle}` : this.pageTitle;
            this.onTitleUpdate();
        },
        shouldGeneratePathFromTitle (newValue) {
            if (newValue) {
                this.generatePathFromTitle();
            }
        }
    },
    created () {
        if (getInitialEditingMode() === EDITING_MODE_CONTENT_EDITOR) {
            this.startCollaborativeEditing();
        }
    },
    mounted () {
        this.initializeTitlePlaceholder();
        if (isDraftStale(this.pageInfo)) {
            this.clearDrafts();
        }
        if (!this.commitMessage) this.updateCommitMessage();
        window.addEventListener('beforeunload', this.onPageUnload);
    },
    destroyed () {
        window.removeEventListener('beforeunload', this.onPageUnload);
        this.stopCollaborativeEditing();
    },
    methods: {
        startCollaborativeEditing () {
            if (!this.collaborativeEditingEnabled || this.collaborationProvider) return;
            const provider = markRaw(new ActionCableProvider({
                channel: 'CollaborativeEditing::WikiPageChannel',
                channelParams: {
                    container_full_path: this.pageInfo.containerFullPath,
                    slug: this.pageInfo.slug
                }
            }));
            provider.connect();
            this.collaborationProvider = provider;
        },
        stopCollaborativeEditing () {
            this.collaborationProvider?.destroy();
            this.collaborationProvider = null;
        },
        async submitForm () {
            this.setMissingFields();
            this.updateDrafts();
            this.isSubmitting = true;
            this.isFormDirty = false;
            this.trackFormSubmit();
            this.trackWikiFormat();
            // Wait until form field values are refreshed
            await this.$nextTick();
            this.$refs.form.$el.submit();
        },
        generatePathFromTitle () {
            const prefix = this.isTemplate ? `${WIKI_TEMPLATES_DIR}/` : '';
            this.path = prefix + this.parentPath + this.pageTitle.replace(/ +/g, '-');
        },
        restorePagePath () {
            this.path = this.initialPath.startsWith(`${WIKI_TEMPLATES_DIR}/`) ? this.path.replace(new RegExp(`^${WIKI_TEMPLATES_DIR}/`), '') : this.initialPath;
        },
        onTitleUpdate () {
            if (!this.placeholderActive) {
                this.frontMatter.title = this.pageTitle;
                this.frontMatter = {
                    ...this.frontMatter
                };
            }
            if ((this.shouldGeneratePathFromTitle || this.isTemplate) && !this.placeholderActive && this.pageTitle !== this.$options.i18n.title.defaultTitle && this.pageTitle.trim().length) {
                this.generatePathFromTitle();
            }
        },
        updateDrafts () {
            const lockVersion = this.pageInfo.lastCommitSha;
            updateDraft(titleAutosaveKey(this.pageInfo), this.title, lockVersion);
            updateDraft(formatAutosaveKey(this.pageInfo), this.format, lockVersion);
            updateDraft(contentAutosaveKey(this.pageInfo), this.content, lockVersion);
            updateDraft(commitAutosaveKey(this.pageInfo), this.commitMessage, lockVersion);
        },
        clearDrafts () {
            clearDraft(titleAutosaveKey(this.pageInfo));
            clearDraft(formatAutosaveKey(this.pageInfo));
            clearDraft(contentAutosaveKey(this.pageInfo));
            clearDraft(commitAutosaveKey(this.pageInfo));
        },
        onPageUnload () {
            if (this.isSubmitting) return;
            if (this.isFormDirty) {
                this.updateDrafts();
            } else {
                this.clearDrafts();
            }
        },
        updateCommitMessage () {
            if (!this.title) return;
            // Replace hyphens with spaces
            let newTitle = this.title.replace(/-+/g, ' ').replace('templates/', '');
            // Replace _sidebar with sidebar
            if (this.isCustomSidebar) {
                newTitle = this.title.replace('_sidebar', 'sidebar');
            }
            const newCommitMessage = sprintf(this.commitMessageI18n, {
                pageTitle: newTitle
            }, false);
            this.commitMessage = newCommitMessage;
        },
        notifyContentEditorActive () {
            this.isContentEditorActive = true;
            this.trackContentEditorLoaded();
            this.startCollaborativeEditing();
        },
        notifyContentEditorInactive () {
            const wasContentEditorActive = this.isContentEditorActive;
            this.isContentEditorActive = false;
            this.stopCollaborativeEditing();
            if (wasContentEditorActive && this.collaborativeEditingEnabled) {
                this.plainTextStartContent = this.content;
            }
        },
        async beforeContentEditor (markdown) {
            if (!this.collaborativeEditingEnabled || markdown === this.plainTextStartContent) return true;
            const discard = await confirmAction(s__('WikiPage|Switching to rich text will discard changes made in plain text. Continue?'), {
                title: s__('WikiPage|Discard plain text changes?'),
                primaryBtnText: s__('WikiPage|Discard changes and switch'),
                cancelBtnText: s__('WikiPage|Keep editing in plain text')
            });
            if (discard) {
                this.content = this.plainTextStartContent;
                // Let MarkdownEditor receive the restored content before it mounts rich text.
                await this.$nextTick();
            }
            return discard;
        },
        trackFormSubmit () {
            // eslint-disable-next-line @gitlab/require-i18n-strings
            trackSavedUsingEditor(this.isContentEditorActive, 'Wiki');
        },
        trackWikiFormat () {
            this.track(WIKI_FORMAT_UPDATED_ACTION, {
                label: WIKI_FORMAT_LABEL,
                extra: {
                    project_path: this.pageInfo.path,
                    old_format: this.pageInfo.format,
                    value: this.format
                }
            });
        },
        trackContentEditorLoaded () {
            this.track(CONTENT_EDITOR_LOADED_ACTION);
        },
        submitFormWithShortcut () {
            this.$refs.form.$el.submit();
        },
        setTemplate (template) {
            this.$refs.markdownEditor.setTemplate(template);
        },
        cancelFormAction () {
            this.isFormDirty = false;
            this.$emit('is-editing', false);
        },
        isPrintableKey (event) {
            // More robust check for printable characters
            // Excludes control keys, function keys, etc.
            if (event.ctrlKey || event.metaKey || event.altKey) {
                return false;
            }
            // Check if it's a single printable character
            // This includes letters, numbers, symbols, space, etc.
            const { key } = event;
            return key.length === 1;
        },
        async initializeTitlePlaceholder () {
            if (this.pageInfo.persisted) return;
            if (!this.pageTitle.endsWith('{new_page_title}')) return;
            this.parentPath = this.pageTitle.replace(/\{new_page_title\}$/, '');
            this.pageTitle = this.placeholderText;
            if (this.shouldGeneratePathFromTitle) {
                this.path = this.parentPath;
            }
            this.placeholderActive = true;
            await this.$nextTick();
            this.positionCursorForPlaceholder();
        },
        positionCursorForPlaceholder () {
            const input = this.$refs.titleInput?.$el || this.$refs.titleInput;
            if (input) {
                input.setSelectionRange(0, 0);
                input.focus();
            }
        },
        handleTitleInput (event) {
            const newValue = (event.target ? event.target.value : event).replace(/[\r\n\u2028\u2029]/g, ' ');
            if (this.placeholderActive) {
                // Check if user has modified the placeholder area
                if (!newValue.includes(this.placeholderText)) {
                    this.placeholderActive = false;
                }
            }
            this.pageTitle = newValue;
        },
        async handleTitleKeydown (event) {
            if (event.key === 'Enter') {
                event.preventDefault();
                return;
            }
            if (this.placeholderActive && this.isPrintableKey(event)) {
                this.placeholderActive = false;
                this.pageTitle = '';
                await this.$nextTick();
                const input = this.$refs.titleInput?.$el || this.$refs.titleInput;
                if (input) {
                    input.setSelectionRange(0, 0);
                }
            }
        },
        handleTitleFocus () {
            if (this.placeholderActive) {
                this.positionCursorForPlaceholder();
            }
        },
        setAutoGeneratedTitle () {
            this.pageTitle = this.$options.i18n.title.defaultTitle;
        },
        setAutoGeneratedPath () {
            const dateSegment = formatDate(Date.now(), AUTO_GENERATED_PATH_DATE_FORMAT);
            const prefix = this.isTemplate ? `${WIKI_TEMPLATES_DIR}/` : '';
            this.path = `${prefix}untitled-${dateSegment}`;
        },
        isAutoGeneratedPath (path) {
            const regex = new RegExp(`^(${WIKI_TEMPLATES_DIR}/)?untitled-\\d{14}$`);
            return regex.test(path);
        },
        setMissingFields () {
            // Ensure empty string with spaces don't prevent the placeholder from being shown
            this.pageTitle = this.pageTitle.trim();
            if (!this.pageTitle.trim().length) {
                this.setAutoGeneratedTitle();
            }
            if (!this.path.trim().length || this.isTemplate && this.isAutoGeneratedPath(this.path)) {
                this.setAutoGeneratedPath();
            }
        },
        handleSave () {
            if (this.useAutoCommitMessage) {
                this.submitForm();
            } else {
                this.commitMessageModalOpen = true;
            }
        },
        async handleSaveMessageModeSelect (value) {
            const useAutoCommitMessage = value === SAVE_MESSAGE.AUTO;
            if (this.useAutoCommitMessage !== useAutoCommitMessage) {
                this.useAutoCommitMessage = useAutoCommitMessage;
                await this.updateCommitMessageModePreference(useAutoCommitMessage);
            }
            this.handleSave();
        },
        async updateCommitMessageModePreference (useAutoCommitMessage) {
            try {
                this.savingPreference = true;
                await this.$apollo.mutate({
                    mutation: updateAutoCommitMessagePreference,
                    variables: {
                        input: {
                            wikiUseAutoCommitMessage: useAutoCommitMessage
                        }
                    },
                    optimisticResponse: {
                        userPreferencesUpdate: {
                            __typename: 'UserPreferencesUpdatePayload',
                            userPreferences: {
                                __typename: 'UserPreferences',
                                wikiUseAutoCommitMessage: useAutoCommitMessage
                            }
                        }
                    },
                    update (cache, { data: { userPreferencesUpdate } }) {
                        cache.updateQuery({
                            query: getAutoCommitMessagePreference
                        }, (existingData)=>produce(existingData, (draft)=>{
                                if (draft?.currentUser?.userPreferences) {
                                    draft.currentUser.userPreferences.wikiUseAutoCommitMessage = userPreferencesUpdate.userPreferences.wikiUseAutoCommitMessage;
                                }
                            }));
                    }
                });
            } catch (e) {
                Sentry.captureException();
            } finally{
                this.savingPreference = false;
            }
        }
    }
};
